/**
 * DXF export-preview DOM-value cap (OOM fix).
 *
 * Definitive root cause (confirmed by CDP native trace + DOM bisection):
 * opening a large streamed DXF hangs (>9 GB OOM / never completes) because
 * of a single multi-second-to-multi-minute SYNCHRONOUS Blink DOM LAYOUT of
 * the export-preview `<textarea>` whose `value` is the FULL exported DXF
 * text. At a 100k-entity slice the textarea value is ~25 MB /
 * `scrollHeight` ~33,554,430 px (Chrome's max), so Blink lays out the
 * entire content synchronously (~28 s main-thread pin); at 685k entities
 * the export is ~6-7x larger → multi-minute layout + >9 GB heap (the giant
 * string + its layout tree). The React render itself is only ~35 ms — it is
 * NOT the cause. `rows={14}` only clips the VISIBLE viewport; Blink still
 * lays out the whole VALUE.
 *
 * The only robust fix is to cap the string that ever reaches the DOM
 * `value` prop. This module owns that cap as a pure, unit-testable function
 * so the OOM guarantee is single-sourced and independent of the (separate)
 * auto-build gate in App.tsx: even if a full build slips through the gate,
 * the DOM textarea never holds more than `DXF_PREVIEW_DOM_CAP` chars, so the
 * giant synchronous layout can never happen.
 */

/**
 * Maximum number of characters of serialised DXF text that may reach the
 * export-preview `<textarea>` DOM `value`.
 *
 * Rationale: 120_000 chars is well under the threshold at which Blink's
 * textarea layout becomes perceptible. A worst-case 120k-char monospace
 * value at ~14 px line-height wraps to at most a few thousand visual rows
 * (far below Chrome's 33.5M-px scroll ceiling), so the synchronous layout
 * completes in under one frame regardless of how huge the underlying export
 * is. The cap engages ONLY above this length, so every small drawing keeps
 * its full live preview byte-for-byte.
 */
export const DXF_PREVIEW_DOM_CAP = 120_000;

/**
 * Cap the export-preview text to {@link DXF_PREVIEW_DOM_CAP} characters,
 * appending a localised truncation marker when (and only when) the input
 * exceeds the cap. Below the cap the input is returned unchanged (identity),
 * so small drawings render their full preview exactly as before.
 *
 * @param previewText - the full serialised DXF preview text.
 * @param formatMarker - produces the localised truncation note given the
 *   number of characters shown and the full length. App.tsx threads
 *   `t("dxfio.preview_truncated", { shown, total })` here so the marker
 *   follows the RU/EN language toggle.
 * @returns the capped text (`<= DXF_PREVIEW_DOM_CAP + marker.length`).
 */
export function capDxfPreviewText(
  previewText: string,
  formatMarker: (shown: number, total: number) => string,
): string {
  if (previewText.length <= DXF_PREVIEW_DOM_CAP) return previewText;
  const marker = formatMarker(DXF_PREVIEW_DOM_CAP, previewText.length);
  return `${previewText.slice(0, DXF_PREVIEW_DOM_CAP)}\n${marker}`;
}
